import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";

const RatePlanChannelForm = ({ handleClose, onSubmit, initialData }) => {
  const [ratePlans, setRatePlans] = useState([]);
  const [channels, setChannels] = useState([]);
  const [activities, setActivities] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);

  useEffect(() => {
    const fetchRatePlans = async () => {
      try {
        const response = await fetch("https://api.esthell.com/RatePlan");
        const data = await response.json();
        if (data.success) {
          setRatePlans(data.data);
        }
      } catch (error) {
        console.error("Error fetching rate plans:", error);
      }
    };

    const fetchChannels = async () => {
      try {
        const response = await fetch(
          "https://api.esthell.com/DistributionChannel",
        );
        const data = await response.json();
        if (data.success) {
          setChannels(data.data);
        }
      } catch (error) {
        console.error("Error fetching channels:", error);
      }
    };

    const fetchActivities = async () => {
      try {
        const response = await fetch("https://api.esthell.com/HmActivity");
        const data = await response.json();
        if (data.success) {
          setActivities(data.data);
        }
      } catch (error) {
        console.error("Error fetching activities:", error);
      }
    };

    const fetchRoomTypes = async () => {
      try {
        const response = await fetch("https://api.esthell.com/HotelRoom");
        const data = await response.json();
        if (data.success) {
          setRoomTypes(data.data);
        }
      } catch (error) {
        console.error("Error fetching room types:", error);
      }
    };

    fetchRatePlans();
    fetchChannels();
    fetchActivities();
    fetchRoomTypes();
  }, []);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      room_type: initialData?.room_type || "",
      rate_plan_id: initialData?.rate_plan_id || "",
      channel_id: initialData?.channel_id || "",
      is_active: initialData?.is_active ?? true,
      activity_type: initialData?.activity_type || "",
    },
  });

  useEffect(() => {
    if (initialData) {
      reset({
        room_type: initialData.room_type,
        rate_plan_id: initialData.rate_plan_id,
        channel_id: initialData.channel_id,
        is_active: initialData.is_active,
        activity_type: initialData.activity_type,
      });
    }
  }, [initialData, reset]);

  const onFormSubmit = (data) => {
    const formData = {
      data: {
        room_type: data.room_type,
        rate_plan_id: parseInt(data.rate_plan_id),
        channel_id: parseInt(data.channel_id),
        is_active: data.is_active,
        activity_type: data.activity_type,
      },
    };

    if (initialData?.id) {
      formData.data.id = initialData.id;
      formData.data.channel_rate_plan_id = initialData.channel_rate_plan_id;
    }

    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{ mb: 3, color: "#1a237e", fontWeight: "bold" }}
      >
        {initialData ? "Edit Rate Plan Channel" : "New Rate Plan Channel"}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="room_type"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Room Type"
                error={!!error}
                helperText={error?.message}
              >
                {roomTypes.map((room) => (
                  <MenuItem key={room.room_id} value={room.room_type}>
                    {room.room_type}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="rate_plan_id"
            control={control}
            rules={{ required: "Rate Plan is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Rate Plan"
                error={!!error}
                helperText={error?.message}
              >
                {ratePlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.rate_plan_id}>
                    {plan.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="channel_id"
            control={control}
            rules={{ required: "Channel is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Channel"
                error={!!error}
                helperText={error?.message}
              >
                {channels.map((channel) => (
                  <MenuItem key={channel.id} value={channel.channel_id}>
                    {channel.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="activity_type"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Activity"
                error={!!error}
                helperText={error?.message}
              >
                {activities.map((activity) => (
                  <MenuItem
                    key={activity.activity_id}
                    value={activity.activity_name}
                  >
                    {activity.activity_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="is_active"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    color="primary"
                  />
                }
                label="Is Active"
              />
            )}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary">
          {initialData ? "Update" : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default RatePlanChannelForm;
